import { useEffect, useMemo, useState } from 'react';
import applicationConstants from '../../../../../Core/Utility/ApplicationConstants';
import { Currency, MarketIndicatorDataCombinationType, UnitOfMeasure } from '../../../../../Generated/Raven-Demeter';
import CurrencyDropdown from '../../../../Components/Form/Inputs/CurrencyDropdown';
import UnitOfMeasureGeneralDropdown from '../../../../Components/Form/Inputs/UnitOfMeasureGeneralDropdown';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from './MarketIndicatorFactorPage.module.scss';

interface IMarketIndicatorFactorDataDisplayProps {
    unitOfMeasure?: UnitOfMeasure;
    currency?: Currency;
    dataCombinationType?: MarketIndicatorDataCombinationType;
    displayUnitOfMeasure?: UnitOfMeasure;
    useSecondary?: boolean;
    handleChange: (unitOfMeasure?: UnitOfMeasure, currency?: Currency, displayUnitOfMeasure?: UnitOfMeasure) => void;
}

const MarketIndicatorFactorDataDisplay: React.FC<IMarketIndicatorFactorDataDisplayProps> = (props: IMarketIndicatorFactorDataDisplayProps) => {
    const [translations] = useLanguage();
    const [displayUnit, setDisplayUnit] = useState<UnitOfMeasure | undefined>();
    const displayUnits = useMemo(() => {
        if (props.dataCombinationType === MarketIndicatorDataCombinationType.Ratio) {
            return [UnitOfMeasure.Percent, UnitOfMeasure.Currency];
        }

        if (props.dataCombinationType === MarketIndicatorDataCombinationType.Multiply) {
            return [UnitOfMeasure.Index];
        }

        return [];
    }, [props.dataCombinationType]);

    useEffect(() => {
        if (props.dataCombinationType === MarketIndicatorDataCombinationType.Ratio) {
            setDisplayUnit(UnitOfMeasure.Percent);
        } else if (props.dataCombinationType === MarketIndicatorDataCombinationType.Multiply) {
            setDisplayUnit(UnitOfMeasure.Index);
        }
    }, [props.dataCombinationType]);

    const hasSecondaryDataSource = !!props.dataCombinationType;

    const overrideUnits = useMemo(() => {
        const defaultUnits: UnitOfMeasure[] = [...applicationConstants.AvailableGeneralUnitOfMeasures];

        if (props.unitOfMeasure && !defaultUnits.includes(props.unitOfMeasure)) {
            defaultUnits.push(props.unitOfMeasure);
        }

        return defaultUnits;
    }, [props.unitOfMeasure]);

    if (!hasSecondaryDataSource) {
        return null;
    }

    return (
        <div className={styles.indicator_add_and_edit_row}>
            <div className={styles.indicator_add_and_edit_dropdown}>
                <UnitOfMeasureGeneralDropdown
                    unitOfMeasure={props.unitOfMeasure}
                    handleChange={(updatedUnitOfMeasure) => props.handleChange(updatedUnitOfMeasure, props.currency, props.displayUnitOfMeasure)}
                    label={translations.words.unit}
                    overrideUnits={overrideUnits}
                    track={{ eventCategory: 'MarketIndicatorsManagement', eventAction: 'UnitOfMeasureChanged' }}
                />
            </div>
            <div className={styles.indicator_add_and_edit_dropdown}>
                <CurrencyDropdown
                    currency={props.currency}
                    handleChange={(updatedCurrency) => props.handleChange(props.unitOfMeasure, updatedCurrency, props.displayUnitOfMeasure)}
                    label={translations.words.currency}
                    track={{ eventCategory: 'MarketIndicatorsManagement', eventAction: 'CurrencyChanged' }}
                />
            </div>
            {!props.useSecondary && !!displayUnits.length && (
                <div className={styles.indicator_add_and_edit_dropdown}>
                    <UnitOfMeasureGeneralDropdown
                        unitOfMeasure={displayUnit}
                        handleChange={(updatedDataCombinationDisplayUnits) => {
                            props.handleChange(props.unitOfMeasure, props.currency, updatedDataCombinationDisplayUnits);
                            setDisplayUnit(updatedDataCombinationDisplayUnits);
                        }}
                        label={translations.words.displayUnits}
                        overrideUnits={displayUnits}
                        hideNativeOption={props.displayUnitOfMeasure !== undefined}
                        track={{ eventCategory: 'MarketIndicatorsManagement', eventAction: 'DisplayUnitsChanged' }}
                    />
                </div>
            )}
        </div>
    );
};

export default MarketIndicatorFactorDataDisplay;
